import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ChangeEvent, FC, memo, useContext, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import LicenseContext from 'src/contexts/LicenseContext';
import { Senario, SpilloverPriority } from 'src/entities/planningEntity';
import { HowToPlanning, PlanningConditions } from 'src/entities/PlanningHistory.entity';
import { migrateRelaxedRules } from 'src/utils/relaxedRule.util';
import { RelaxedRuleVo } from 'src/vo/RelaxedRule.vo';

import { buildPlanningConditionsSnapshot, DispatchScenario, planningPriorityToPriorityMode } from './buildPlanningConditionsSnapshot';

type DispatchModel = 'opus' | 'repro' | 'fusion';

type Props = {
  screenName: string;
  updateBalancedLoading: (balancedLoading: boolean) => void;
  updateExcludeToll: (bool: boolean) => void;
  updateIncludeFutureDeliveries: (bool: boolean) => void;
  updateAutoSplitOrders: (bool: boolean) => void;
  updateDontExchangePreroute: (bool: boolean) => void;
  updateConcurrentAllOrNothing: (bool: boolean) => void;
  updateExpandWorkingStartMinutes: (minutes: number) => void;
  updateExpandWorkingEndMinutes: (minutes: number) => void;
  updateExpandLoadStartMinutes: (minutes: number) => void;
  updateExpandLoadEndMinutes: (minutes: number) => void;
  updateExpandUnloadStartMinutes: (minutes: number) => void;
  updateExpandUnloadEndMinutes: (minutes: number) => void;
  updateExpandMaxVolumeRate: (rate: number) => void;
  updateExpandMaxLoadCapacityRate: (rate: number) => void;
  planningRequest: (shiftIds: number[], orderIds: number[]) => void;
  onClose: () => void;
  selectedDeliveryIds: number[];
  selectedOrderIds: number[];
  startOn: string;
  relaxedRules: RelaxedRuleVo[];
  selectedRelaxedRules: RelaxedRuleVo[];
  setSelectedRelaxedRules: (value: RelaxedRuleVo[]) => void;
  setPriorityLargeTrucks: (priority: number) => void;
  priorityLargeTrucks: number;
  updateActivateIncrementalMaxNumPistons: (bool: boolean) => void;
  updateSenario: (senario: Senario) => void;
  updateSpilloverPriority: (spilloverPriority: SpilloverPriority | undefined) => void;
  updateDisableGlobalOpt: (bool: boolean) => void;
  updateTraceCompanyId: (id: number) => void;
  /**
   * 選択済みのうち、呼び出し元のフィルタで非表示になっている件数。
   * 未指定なら警告を出さない（V3Plans / V3Timeline / TrunkTransportation は未指定のまま）。
   */
  hiddenSelectedOrderCount?: number;
  /**
   * 画面設定 (buildPlanningConditionsSnapshot の返り値) が変わるたびに呼ばれる。
   * シナリオ履歴 (planning_setting.planningConditions) を必要とするのは Map だけなので
   * optional とし、localStorage への保存は従来どおり本コンポーネント自身が行う。
   */
  onPlanningConditionsChange?: (conditions: PlanningConditions) => void;
};

// Why not value リネーム: value (opus/repro/fusion) を変えると saved.dispatchModel / localStorage
//   互換が壊れるため据え置き、label / description のみ #286 の新仕様へ差し替える。
const dispatchModelDefs: { value: DispatchModel; label: string; description: string }[] = [
  { value: 'opus', label: 'LOG-3.0', description: '標準の配車モデル' },
  { value: 'repro', label: 'LOG-3.0 Trace', description: '過去の配車傾向を再現' },
  { value: 'fusion', label: 'LOG-3.0 Opus', description: '過去の配車傾向を基に最適化' },
];

// 配車シナリオ(#296)。LOG-3.0 Opus のときだけ選べる。
// How: 画面の 3 択を senario + spillover_priority の組に展開する。priority は spillover 専用で、
//   通常配車では undefined を送る (API 側も spillover 以外では config に載せない)。
// Why not 全モデル共通の選択肢にしない: spillover は g-opt 前提のシナリオで、
//   標準(setcover)や g-opt 無効の Trace と組み合わせても意味を持たない。
const dispatchScenarioDefs: {
  value: DispatchScenario;
  label: string;
  description: string;
  spilloverPriority?: SpilloverPriority;
}[] = [
  { value: 'normal', label: '通常配車', description: '全案件を対象に配車' },
  {
    value: 'spillover_duration',
    label: '超過分の調整配車（時間最適化）',
    description: '超過分について、作業時間を短くするように再配分',
    spilloverPriority: 'duration',
  },
  {
    value: 'spillover_trucks',
    label: '超過分の調整配車（台数最適化）',
    description: '超過分について、台数を少なくするように再配分',
    spilloverPriority: 'trucks',
  },
];

// spillover では quantity_sequence を緩和できない (アルゴリズムがエラーで停止する。MD §4)。
const QUANTITY_RELAXED_RULE: RelaxedRuleVo = '数量';

// 配車シナリオを選べるモデル。value 'fusion' がラベル「LOG-3.0 Opus」。
const SCENARIO_SELECTABLE_DISPATCH_MODEL: DispatchModel = 'fusion';
const DEFAULT_DISPATCH_SCENARIO: DispatchScenario = 'normal';

// 学習配車 (senario: apprentice) を使わない標準モデル。
// 注意: value 'opus' はラベル「LOG-3.0」= 標準 (setcover) を指す。
//       学習配車の「LOG-3.0 Opus」は value 'fusion' の方であり、名前が交差している。
const STANDARD_DISPATCH_MODEL: DispatchModel = 'opus';

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 4,
          height: 16,
          borderRadius: 1,
          bgcolor: 'primary.main',
          flexShrink: 0,
        }}
      />
      <Typography variant="subtitle2" fontWeight="bold">
        {children}
      </Typography>
    </Box>
  );
}

type ConstraintRowProps = {
  label: string;
  value: number;
  onChange: (v: number) => void;
  unit: string;
  max?: number;
  disabled?: boolean;
};

function ConstraintRow({ label, value, onChange, unit, max = 120, disabled = false }: ConstraintRowProps) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: '4rem', flexShrink: 0 }}
      >
        {label}
      </Typography>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => { if (typeof v === 'number') onChange(v); }}
        step={5}
        min={0}
        max={max}
        disabled={disabled}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="number"
        inputProps={{ min: 0, max, step: 1 }}
        value={value}
        disabled={disabled}
        onChange={(e) => {
          const n = Math.min(max, Math.max(0, Number(e.target.value) || 0));
          onChange(n);
        }}
        sx={{ width: '5rem' }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: '2.5rem', flexShrink: 0 }}>
        {unit}
      </Typography>
    </Stack>
  );
}

type ToggleSwitchRowProps = {
  label: string;
  tooltip?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

function ToggleSwitchRow({ label, tooltip, checked, onChange }: ToggleSwitchRowProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 0.75,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </Tooltip>
        )}
      </Box>
      <Switch
        size="small"
        checked={checked}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
      />
    </Box>
  );
}

const PlanningPresenter: FC<Props> = memo(
  ({
    screenName,
    updateBalancedLoading,
    updateExcludeToll,
    updateIncludeFutureDeliveries,
    updateAutoSplitOrders,
    updateDontExchangePreroute,
    updateConcurrentAllOrNothing,
    updateExpandWorkingStartMinutes,
    updateExpandWorkingEndMinutes,
    updateExpandLoadStartMinutes,
    updateExpandLoadEndMinutes,
    updateExpandUnloadStartMinutes,
    updateExpandUnloadEndMinutes,
    updateExpandMaxVolumeRate,
    updateExpandMaxLoadCapacityRate,
    planningRequest,
    onClose,
    selectedDeliveryIds,
    selectedOrderIds,
    relaxedRules,
    selectedRelaxedRules,
    setSelectedRelaxedRules,
    setPriorityLargeTrucks,
    priorityLargeTrucks,
    updateActivateIncrementalMaxNumPistons,
    updateSenario,
    updateSpilloverPriority,
    updateDisableGlobalOpt,
    updateTraceCompanyId,
    hiddenSelectedOrderCount,
    onPlanningConditionsChange,
  }) => {
    const licenseContext = useContext(LicenseContext);

    const [howToPlanning, setHowToPlanning] = useState<HowToPlanning>('最適化');
    const [priorityMode, setPriorityMode] = useState<'none' | 'nearby' | 'far'>('none');
    const [dispatchModel, setDispatchModel] = useState<DispatchModel>('opus');
    const [dispatchScenario, setDispatchScenario] = useState<DispatchScenario>(DEFAULT_DISPATCH_SCENARIO);
    type VehicleSizePreference = 'none' | 'small' | 'large';
    const [vehicleSizePreference, setVehicleSizePreference] = useState<VehicleSizePreference>('none');
    const [availabilityOfExpressway, setAvailabilityOfExpressway] = useState<boolean>(false);
    const [includeFutureDeliveries, setIncludeFutureDeliveries] = useState<boolean>(false);
    const [autoSplitOrders, setAutoSplitOrders] = useState<boolean>(false);
    const [dontExchangePreroute, setDontExchangePreroute] = useState<boolean>(true);
    const [concurrentAllOrNothing, setConcurrentAllOrNothing] = useState<boolean>(true);
    const [activateIncrementalMaxNumPistons, setActivateIncrementalMaxNumPistons] = useState<boolean>(false);
    const [expandWorkingTimeStartMinutes, setExpandWorkingTimeStartMinutes] = useState<number>(0);
    const [expandWorkingTimeEndMinutes, setExpandWorkingTimeEndMinutes] = useState<number>(0);
    const [expandLoadStartMinutes, setExpandLoadStartMinutes] = useState<number>(0);
    const [expandLoadEndMinutes, setExpandLoadEndMinutes] = useState<number>(0);
    const [expandUnloadStartMinutes, setExpandUnloadStartMinutes] = useState<number>(0);
    const [expandUnloadEndMinutes, setExpandUnloadEndMinutes] = useState<number>(0);
    const [expandMaxVolumeRate, setExpandMaxVolumeRate] = useState<number>(0);
    const [expandMaxLoadCapacityRate, setExpandMaxLoadCapacityRate] = useState<number>(0);
    const [isExecuting, setIsExecuting] = useState<boolean>(false);
    const [constraintExpanded, setConstraintExpanded] = useState<boolean>(false);

    const useAiPlanning = licenseContext.config?.use_ai_planning === true;

    // How: モデル選択(#286)と配車シナリオ(#296)の「実際に送る値」をここで一本化する。
    //      送信 useEffect と画面表示(「数量」のロック)が別々に導出すると食い違うため。
    // Why not: AI配車ライセンスが無い間はモデル選択 UI を描画しないので、dispatchModel の値に
    //      関わらず標準モデル(setcover)として扱う。
    const effectiveModel: DispatchModel = useAiPlanning ? dispatchModel : STANDARD_DISPATCH_MODEL;
    // 配車シナリオは LOG-3.0 Opus 配下だけで有効。他モデルでは選択値に関わらず通常配車。
    // (state のリセットは下の useEffect が行うが、同一コミット内では反映されないためここでも見る)
    const effectiveScenario: DispatchScenario = effectiveModel === SCENARIO_SELECTABLE_DISPATCH_MODEL
      ? dispatchScenario
      : DEFAULT_DISPATCH_SCENARIO;
    const spilloverPriority = dispatchScenarioDefs
      .find((it) => it.value === effectiveScenario)?.spilloverPriority;
    const isSpilloverScenario = spilloverPriority !== undefined;

    // 制約セクションに値が設定されている場合は自動で開く（閉じるのはユーザー操作のみ）
    const hasConstraintValues = useMemo(
      () =>
        selectedRelaxedRules.length > 0 ||
        expandWorkingTimeStartMinutes > 0 ||
        expandWorkingTimeEndMinutes > 0 ||
        expandLoadStartMinutes > 0 ||
        expandLoadEndMinutes > 0 ||
        expandUnloadStartMinutes > 0 ||
        expandUnloadEndMinutes > 0 ||
        expandMaxLoadCapacityRate > 0 ||
        expandMaxVolumeRate > 0,
      [
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxLoadCapacityRate,
        expandMaxVolumeRate,
      ],
    );

    useEffect(() => {
      if (hasConstraintValues) setConstraintExpanded(true);
    }, [hasConstraintValues]);

    // How: effectiveModel / spilloverPriority を senario / spillover_priority /
    //      disable_global_opt / trace_company_id へマッピングする(#286, #296)。
    // Why not: localStorage 復元 (setDispatchModel) 側をガードしない。localStorage は origin 単位で
    //          保持され、config 取得前の初回レンダーでは useAiPlanning が false になる。復元を止めると
    //          その直後に走る保存 useEffect が dispatchModel: 'opus' で上書きし、ライセンスを持つ
    //          ユーザーの選択まで消えてしまう。送出の入口だけを塞ぐ。
    useEffect(() => {
      const companyId = licenseContext.config?.selected_company_id ?? 0;
      updateSpilloverPriority(spilloverPriority);
      if (spilloverPriority) {
        // 超過分の調整配車: 固定ルートの積載オーバー分を傭車へ移す。学習モデルは使わない
        updateSenario('spillover');
        updateTraceCompanyId(0);
        updateDisableGlobalOpt(false);
      } else if (effectiveModel === 'repro') {
        // LOG-3.0 Trace: apprentice + g-opt無効
        updateSenario('apprentice');
        updateTraceCompanyId(companyId);
        updateDisableGlobalOpt(true);
      } else if (effectiveModel === 'fusion') {
        // LOG-3.0 Opus: apprentice + g-opt有効
        updateSenario('apprentice');
        updateTraceCompanyId(companyId);
        updateDisableGlobalOpt(false);
      } else {
        // LOG-3.0 (opus): 標準 setcover
        updateSenario('setcover');
        updateTraceCompanyId(0);
        updateDisableGlobalOpt(false);
      }
    }, [effectiveModel, spilloverPriority, licenseContext.config?.selected_company_id, updateSenario, updateSpilloverPriority, updateTraceCompanyId, updateDisableGlobalOpt]);

    // How: 超過分の調整配車では「数量」の緩和を選べない。選択済みなら外す。
    //      緩和したまま実行するとアルゴリズムがエラーで停止し、原因が画面から分からないため。
    // Why not dispatchScenario を直接見ない: ライセンスが無いと配車シナリオの選択 UI が出ないため、
    //      localStorage に残った spillover でロックすると理由も直し方も画面から辿れなくなる。
    //      送信値と同じ effectiveScenario 由来の spilloverPriority で判定する。
    useEffect(() => {
      if (!isSpilloverScenario) return;
      if (!selectedRelaxedRules.includes(QUANTITY_RELAXED_RULE)) return;

      setSelectedRelaxedRules(selectedRelaxedRules.filter((it) => it !== QUANTITY_RELAXED_RULE));
    }, [isSpilloverScenario, selectedRelaxedRules, setSelectedRelaxedRules]);

    // How: モデルを LOG-3.0 Opus 以外へ切り替えたら配車シナリオを通常配車へ戻す。
    //      見えない状態で spillover が残り、localStorage 経由で次回に持ち越されるのを防ぐ。
    // Why not useAiPlanning でもガードする: config 取得前の初回レンダーは useAiPlanning=false に
    //      なるため、ここでライセンスまで見るとライセンス保有者の選択を毎回消してしまう(#292 と同じ罠)。
    //      送出の抑止は上の effectiveScenario が担う。
    useEffect(() => {
      if (dispatchModel !== SCENARIO_SELECTABLE_DISPATCH_MODEL) setDispatchScenario(DEFAULT_DISPATCH_SCENARIO);
    }, [dispatchModel]);

    useEffect(() => {
      updateBalancedLoading(howToPlanning === '平準化');
    }, [howToPlanning, updateBalancedLoading]);

    // vehicleSizePreference → priorityLargeTrucks マッピング
    useEffect(() => {
      if (vehicleSizePreference === 'none') setPriorityLargeTrucks(0.5);
      else if (vehicleSizePreference === 'small') setPriorityLargeTrucks(0);
      else setPriorityLargeTrucks(1.0);
    }, [vehicleSizePreference, setPriorityLargeTrucks]);

    useEffect(() => {
      updateExcludeToll(!availabilityOfExpressway);
    }, [availabilityOfExpressway, updateExcludeToll]);

    useEffect(() => {
      updateIncludeFutureDeliveries(includeFutureDeliveries);
    }, [includeFutureDeliveries, updateIncludeFutureDeliveries]);

    useEffect(() => {
      updateAutoSplitOrders(autoSplitOrders);
    }, [autoSplitOrders, updateAutoSplitOrders]);

    useEffect(() => {
      updateDontExchangePreroute(dontExchangePreroute);
    }, [dontExchangePreroute, updateDontExchangePreroute]);

    useEffect(() => {
      updateConcurrentAllOrNothing(concurrentAllOrNothing);
    }, [concurrentAllOrNothing, updateConcurrentAllOrNothing]);

    useEffect(() => {
      updateActivateIncrementalMaxNumPistons(activateIncrementalMaxNumPistons);
    }, [activateIncrementalMaxNumPistons, updateActivateIncrementalMaxNumPistons]);

    useEffect(() => {
      updateExpandWorkingStartMinutes(expandWorkingTimeStartMinutes);
    }, [expandWorkingTimeStartMinutes, updateExpandWorkingStartMinutes]);

    useEffect(() => {
      updateExpandWorkingEndMinutes(expandWorkingTimeEndMinutes);
    }, [expandWorkingTimeEndMinutes, updateExpandWorkingEndMinutes]);

    useEffect(() => {
      updateExpandLoadStartMinutes(expandLoadStartMinutes);
    }, [expandLoadStartMinutes, updateExpandLoadStartMinutes]);

    useEffect(() => {
      updateExpandLoadEndMinutes(expandLoadEndMinutes);
    }, [expandLoadEndMinutes, updateExpandLoadEndMinutes]);

    useEffect(() => {
      updateExpandUnloadStartMinutes(expandUnloadStartMinutes);
    }, [expandUnloadStartMinutes, updateExpandUnloadStartMinutes]);

    useEffect(() => {
      updateExpandUnloadEndMinutes(expandUnloadEndMinutes);
    }, [expandUnloadEndMinutes, updateExpandUnloadEndMinutes]);

    useEffect(() => {
      updateExpandMaxVolumeRate(expandMaxVolumeRate);
    }, [expandMaxVolumeRate, updateExpandMaxVolumeRate]);

    useEffect(() => {
      updateExpandMaxLoadCapacityRate(expandMaxLoadCapacityRate);
    }, [expandMaxLoadCapacityRate, updateExpandMaxLoadCapacityRate]);

    // 制約選択時に緩和値をリセット
    useEffect(() => {
      if (!selectedRelaxedRules?.length) return;
      if (selectedRelaxedRules.includes('労働時間')) {
        setExpandWorkingTimeStartMinutes(0);
        setExpandWorkingTimeEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('指定時間')) {
        setExpandLoadStartMinutes(0);
        setExpandLoadEndMinutes(0);
        setExpandUnloadStartMinutes(0);
        setExpandUnloadEndMinutes(0);
      }
      if (selectedRelaxedRules.includes('重量')) {
        setExpandMaxLoadCapacityRate(0);
      }
      if (selectedRelaxedRules.includes('体積')) {
        setExpandMaxVolumeRate(0);
      }
    }, [selectedRelaxedRules]);

    // localStorage 復元
    useEffect(() => {
      const json = localStorage.getItem('PlanningPresenterSettings');
      if (!json) return;

      const saved = JSON.parse(json) as Partial<PlanningConditions & {
        priorityMode?: 'none' | 'nearby' | 'far';
        vehicleSizePreference?: VehicleSizePreference;
        dispatchModel?: DispatchModel;
      }>;

      if (saved.howToPlanning === '最適化' || saved.howToPlanning === '平準化') {
        setHowToPlanning(saved.howToPlanning);
      }
      if (saved.availabilityOfExpressway !== undefined) setAvailabilityOfExpressway(saved.availabilityOfExpressway);
      if (saved.includeFutureDeliveries !== undefined) setIncludeFutureDeliveries(saved.includeFutureDeliveries);
      // Why not splitOrders も復元する: 案件分割は配車結果を実際に変えるので、
      // 前回 ON にしたことを忘れたまま次の配車が走らないよう毎回 false から始める。
      // V2Plans も同じ localStorage キーを読みながら setAutoSplitOrders(false) で固定している
      // (V2Plans/presenters/PlanningPresenter.tsx:420)。保存はする（履歴に何を選んで実行したかは残す）
      if (saved.dontExchangePreroute !== undefined) setDontExchangePreroute(saved.dontExchangePreroute);
      if (saved.concurrentAllOrNothing !== undefined) setConcurrentAllOrNothing(saved.concurrentAllOrNothing);
      if (saved.activateIncrementalMaxNumPistons !== undefined) setActivateIncrementalMaxNumPistons(saved.activateIncrementalMaxNumPistons);
      // #329 で改名した緩和ルール名。旧名のまま保存されているブラウザでも選択が消えないよう寄せる
      // （この復元のあと下の保存 effect が新しい名前で書き直すので、次回以降は変換不要になる）
      if (saved.selectedRelaxedRules) {
        setSelectedRelaxedRules(migrateRelaxedRules(saved.selectedRelaxedRules));
      }
      if (saved.expandWorkingTimeStartMinutes !== undefined) setExpandWorkingTimeStartMinutes(saved.expandWorkingTimeStartMinutes);
      if (saved.expandWorkingTimeEndMinutes !== undefined) setExpandWorkingTimeEndMinutes(saved.expandWorkingTimeEndMinutes);
      if (saved.expandLoadStartMinutes !== undefined) setExpandLoadStartMinutes(saved.expandLoadStartMinutes);
      if (saved.expandLoadEndMinutes !== undefined) setExpandLoadEndMinutes(saved.expandLoadEndMinutes);
      if (saved.expandUnloadStartMinutes !== undefined) setExpandUnloadStartMinutes(saved.expandUnloadStartMinutes);
      if (saved.expandUnloadEndMinutes !== undefined) setExpandUnloadEndMinutes(saved.expandUnloadEndMinutes);
      if (saved.expandMaxVolumeRate !== undefined) setExpandMaxVolumeRate(saved.expandMaxVolumeRate);
      if (saved.expandMaxLoadCapacityRate !== undefined) setExpandMaxLoadCapacityRate(saved.expandMaxLoadCapacityRate);
      // Why not 新キー(planningPriority)だけを見る: buildPlanningConditionsSnapshot 導入後は
      //   planningPriority (日本語ラベル) で保存されるが、導入前に保存された旧キー(priorityMode)
      //   のデータがユーザーのブラウザに残っている間はそちらも復元できないと設定が消えて見える。
      if (saved.planningPriority) {
        setPriorityMode(planningPriorityToPriorityMode(saved.planningPriority));
      } else if (saved.priorityMode) {
        setPriorityMode(saved.priorityMode);
      }
      if (saved.vehicleSizePreference) setVehicleSizePreference(saved.vehicleSizePreference);
      if (saved.dispatchModel) setDispatchModel(saved.dispatchModel);
      if (saved.dispatchScenario) setDispatchScenario(saved.dispatchScenario);
    }, [licenseContext.config, setSelectedRelaxedRules]);

    // localStorage 保存 + 履歴 snapshot (onPlanningConditionsChange) への通知
    // How: 両者が別々に object を手組みすると食い違うため、buildPlanningConditionsSnapshot の
    //      返り値1つだけを両方の送り先に渡す（RED 5: 同じ snapshot を受け取る）。
    useEffect(() => {
      const snapshot = buildPlanningConditionsSnapshot({
        howToPlanning,
        priorityMode,
        vehicleSizePreference,
        dispatchModel,
        dispatchScenario,
        availabilityOfExpressway,
        includeFutureDeliveries,
        autoSplitOrders,
        dontExchangePreroute,
        concurrentAllOrNothing,
        activateIncrementalMaxNumPistons,
        selectedRelaxedRules,
        expandWorkingTimeStartMinutes,
        expandWorkingTimeEndMinutes,
        expandLoadStartMinutes,
        expandLoadEndMinutes,
        expandUnloadStartMinutes,
        expandUnloadEndMinutes,
        expandMaxVolumeRate,
        expandMaxLoadCapacityRate,
      });
      localStorage.setItem('PlanningPresenterSettings', JSON.stringify(snapshot));
      onPlanningConditionsChange?.(snapshot);
    }, [
      howToPlanning,
      priorityMode,
      vehicleSizePreference,
      availabilityOfExpressway,
      includeFutureDeliveries,
      autoSplitOrders,
      dontExchangePreroute,
      concurrentAllOrNothing,
      activateIncrementalMaxNumPistons,
      selectedRelaxedRules,
      expandWorkingTimeStartMinutes,
      expandWorkingTimeEndMinutes,
      expandLoadStartMinutes,
      expandLoadEndMinutes,
      expandUnloadStartMinutes,
      expandUnloadEndMinutes,
      expandMaxVolumeRate,
      expandMaxLoadCapacityRate,
      dispatchModel,
      dispatchScenario,
      onPlanningConditionsChange,
    ]);

    const onClick = () => {
      if (isExecuting) return;
      setIsExecuting(true);
      ReactGA.event('executePlanning', { screen_name: screenName, button_name: '自動配車' });
      onClose();
      planningRequest(selectedDeliveryIds, selectedOrderIds);
    };

    const handleChipToggle = (rule: RelaxedRuleVo) => {
      if (selectedRelaxedRules.includes(rule)) {
        setSelectedRelaxedRules(selectedRelaxedRules.filter((r) => r !== rule));
      } else {
        setSelectedRelaxedRules([...selectedRelaxedRules, rule]);
      }
    };

    return (
      <>
        <DialogTitle sx={{ pb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <TuneRoundedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold" lineHeight={1.3}>
              自動配車の設定
            </Typography>
          </Stack>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
            配車条件を設定して自動割り当てを実行します
          </Typography>
        </DialogTitle>
        <DialogContent
          sx={{
            overflowY: 'auto',
            maxHeight: '80vh',
            px: 3,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {(hiddenSelectedOrderCount ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              選択中のうち {hiddenSelectedOrderCount} 件は現在のフィルタで非表示です。非表示の案件も配車対象に含まれます。
            </Alert>
          )}

          {/* Section 1: 計算方法 */}
          <Box>
            <SectionLabel>計算方法</SectionLabel>
            <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
              <Select
                value={howToPlanning}
                onChange={(e: SelectChangeEvent) => setHowToPlanning(e.target.value as HowToPlanning)}
              >
                <MenuItem value="最適化">最適化 — トラック台数と時間距離を最小化</MenuItem>
                <MenuItem value="平準化">平準化 — 割り付ける荷物を均等化</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Section 2: 優先設定 */}
          <Box>
            <SectionLabel>優先設定</SectionLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  エリア優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={priorityMode}
                  onChange={(_, v: 'none' | 'nearby' | 'far' | null) => {
                    if (v !== null) setPriorityMode(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'nearby', label: '近隣' },
                    { value: 'far', label: '遠方' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  車両サイズ優先
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={vehicleSizePreference}
                  onChange={(_, v: VehicleSizePreference | null) => {
                    if (v !== null) setVehicleSizePreference(v);
                  }}
                >
                  {([
                    { value: 'none', label: '指定なし' },
                    { value: 'small', label: '小型' },
                    { value: 'large', label: '大型' },
                  ] as const).map((opt) => (
                    <ToggleButton key={opt.value} value={opt.value} sx={{ fontSize: '0.7rem' }}>
                      {opt.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="有料道路使用"
                  checked={availabilityOfExpressway}
                  onChange={setAvailabilityOfExpressway}
                />
              </Box>

              <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                <ToggleSwitchRow
                  label="宵積み有効"
                  checked={includeFutureDeliveries}
                  onChange={setIncludeFutureDeliveries}
                />
              </Box>
            </Box>
          </Box>

          {/* Section 3: 配車方式 */}
          <Box>
            <SectionLabel>配車方式</SectionLabel>
            <Stack spacing={1} sx={{ mt: 1 }}>
              {/* ToggleSwitchRow × 2: 2カラムグリッド */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <ToggleSwitchRow
                  label="既存割り当てを維持"
                  tooltip="新しいオーダーを追加配車する際、既存のドライバー割り当てを維持するか、ドライバー間で割り当てを変更するかを選択できます。"
                  checked={dontExchangePreroute}
                  onChange={setDontExchangePreroute}
                />
                <ToggleSwitchRow
                  label="回転数を平準化"
                  tooltip="各車両の回転数をなるべく均等に配分します"
                  checked={activateIncrementalMaxNumPistons}
                  onChange={setActivateIncrementalMaxNumPistons}
                />
              </Box>
              {/* Checkbox × 2: 縦並び・左インデント */}
              <Stack spacing={0} sx={{ pl: 0.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={autoSplitOrders}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setAutoSplitOrders(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">積載量超過時に案件を自動分割</Typography>}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={concurrentAllOrNothing}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => setConcurrentAllOrNothing(e.target.checked)}
                      sx={{ py: 0.25 }}
                    />
                  }
                  label={<Typography variant="body2">同一ロケーションで複数台になる場合は割り当てない</Typography>}
                />
              </Stack>
            </Stack>
          </Box>

          {/* Section 4: モデル選択 (use_ai_planning=trueのときのみ) */}
          {useAiPlanning && (
            <Box>
              <SectionLabel>モデル</SectionLabel>
              <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                <Select
                  value={dispatchModel}
                  onChange={(e: SelectChangeEvent) => setDispatchModel(e.target.value as DispatchModel)}
                  renderValue={(value) => {
                    const model = dispatchModelDefs.find((m) => m.value === value);
                    // How: 選択値のラベルは他の Select(計算方法)と同じ既定サイズ(body1)に揃える。
                    //      説明は body2 グレーで従属表示。ドロップダウン内 MenuItem は body2/caption のまま。
                    return (
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>{model?.label}</Typography>
                        <Typography variant="body2" color="text.secondary">— {model?.description}</Typography>
                      </Stack>
                    );
                  }}
                >
                  {dispatchModelDefs.map((model) => (
                    <MenuItem key={model.value} value={model.value}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{model.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{model.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* 配車シナリオ (LOG-3.0 Opus のときだけ) */}
              {dispatchModel === SCENARIO_SELECTABLE_DISPATCH_MODEL && (
                <Box sx={{ mt: 2 }}>
                  <SectionLabel>配車シナリオ</SectionLabel>
                  <FormControl fullWidth variant="standard" sx={{ mt: 1 }}>
                    <Select
                      value={dispatchScenario}
                      onChange={(e: SelectChangeEvent) => setDispatchScenario(e.target.value as DispatchScenario)}
                      renderValue={(value) => {
                        const scenario = dispatchScenarioDefs.find((it) => it.value === value);
                        return (
                          <Stack direction="row" alignItems="baseline" spacing={1}>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>{scenario?.label}</Typography>
                            <Typography variant="body2" color="text.secondary">— {scenario?.description}</Typography>
                          </Stack>
                        );
                      }}
                    >
                      {dispatchScenarioDefs.map((scenario) => (
                        <MenuItem key={scenario.value} value={scenario.value}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{scenario.label}</Typography>
                            <Typography variant="caption" color="text.secondary">{scenario.description}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}

          {/* Section 5: 制約 (Accordion) */}
          <Accordion
            disableGutters
            elevation={0}
            expanded={constraintExpanded}
            onChange={(_, expanded) => setConstraintExpanded(expanded)}
            sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
              <SectionLabel>制約</SectionLabel>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {/* 無効にする制約 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    無効にする制約
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([...relaxedRules])}
                    >
                      全選択
                    </Button>
                    <Button
                      size="small"
                      variant="text"
                      sx={{ fontSize: '0.7rem', minWidth: 0, p: 0.5 }}
                      onClick={() => setSelectedRelaxedRules([])}
                    >
                      全解除
                    </Button>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {relaxedRules.map((rule) => {
                    const isLocked = isSpilloverScenario && rule === QUANTITY_RELAXED_RULE;
                    // How: ロック中は未選択の見た目に固定する。親 state から「数量」を外す useEffect は
                    //      paint の後に走るため、切り替え直後の 1 フレームだけ
                    //      「選択済み(filled/primary)かつ無効」のチップが描画されてちらつく。
                    const isSelected = selectedRelaxedRules.includes(rule) && !isLocked;
                    return (
                      <Chip
                        key={rule}
                        label={rule}
                        size="small"
                        disabled={isLocked}
                        onClick={isLocked ? undefined : () => handleChipToggle(rule)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ fontSize: '0.7rem' }}
                      />
                    );
                  })}
                </Box>
                {/* Why not tooltip だけにしない: 無効化した Chip は pointer-events が切れて hover が
                    届かず、理由に気づけない。常時見えるテキストで出す */}
                {isSpilloverScenario && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    超過分の調整配車では「数量」を緩和できません（超過の解消が目的のため）
                  </Typography>
                )}
              </Box>

              {/* 制約緩和 */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight="medium">
                    制約緩和
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    sx={{ fontSize: '0.65rem', minWidth: 0, p: '2px 6px', lineHeight: 1 }}
                    onClick={() => {
                      setExpandWorkingTimeStartMinutes(0);
                      setExpandWorkingTimeEndMinutes(0);
                      setExpandLoadStartMinutes(0);
                      setExpandLoadEndMinutes(0);
                      setExpandUnloadStartMinutes(0);
                      setExpandUnloadEndMinutes(0);
                      setExpandMaxLoadCapacityRate(0);
                      setExpandMaxVolumeRate(0);
                    }}
                  >
                    リセット
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務開始"
                      value={expandWorkingTimeStartMinutes}
                      onChange={setExpandWorkingTimeStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="勤務終了"
                      value={expandWorkingTimeEndMinutes}
                      onChange={setExpandWorkingTimeEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('労働時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷開始"
                      value={expandLoadStartMinutes}
                      onChange={setExpandLoadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="集荷締切"
                      value={expandLoadEndMinutes}
                      onChange={setExpandLoadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品開始"
                      value={expandUnloadStartMinutes}
                      onChange={setExpandUnloadStartMinutes}
                      unit="分前"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="納品締切"
                      value={expandUnloadEndMinutes}
                      onChange={setExpandUnloadEndMinutes}
                      unit="分後"
                      disabled={selectedRelaxedRules.includes('指定時間')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載重量"
                      value={expandMaxLoadCapacityRate}
                      onChange={setExpandMaxLoadCapacityRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('重量')}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 240 }}>
                    <ConstraintRow
                      label="積載体積"
                      value={expandMaxVolumeRate}
                      onChange={setExpandMaxVolumeRate}
                      unit="%"
                      max={100}
                      disabled={selectedRelaxedRules.includes('体積')}
                    />
                  </Box>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </DialogContent>

        {/* Footer */}
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button variant="text" size="small" onClick={onClose} color="inherit">
            キャンセル
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<LocalShippingRoundedIcon />}
            onClick={onClick}
            disabled={isExecuting}
          >
            実行する
          </Button>
        </Box>
      </>
    );
  }
);

export default PlanningPresenter;
